import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    useTheme,
    Grid,
    Chip,
    Divider,
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    ReferenceLine,
    Label,
    Legend,
} from 'recharts';
import { 
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Timeline as TimelineIcon,
    Speed as SpeedIcon
} from '@mui/icons-material';
import { Device } from '../../types/Device';
import { useDeviceRealtime } from '../../hooks/useDeviceRealtime';
import { format } from 'date-fns';

interface DataVisualizationProps {
    type: 'gnss' | 'ocxo';
    device: Device;
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
    const theme = useTheme();
    
    if (active && payload && payload.length) {
        return (
            <Paper
                elevation={3}
                sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    '& p': { mb: 0.5 },
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Time: {format(new Date(label), 'HH:mm:ss')}
                </Typography>
                {payload.map((entry: any, index: number) => (
                    <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                        {entry.dataKey}: {entry.value.toFixed(4)}
                    </Typography>
                ))}
            </Paper>
        );
    }

    return null;
};

const DataVisualization: React.FC<DataVisualizationProps> = ({ type, device }) => {
    const theme = useTheme();
    const { realtimeData, chartData, getActiveGnssReceivers } = useDeviceRealtime(device.id);

    // Prepare chart data based on type
    const { data, hasRealTimeData, activeReceivers } = useMemo(() => {
        if (type === 'gnss') {
            const receivers = getActiveGnssReceivers();
            
            if (receivers.length > 0) {
                // Use real-time accumulated data
                const maxLength = Math.max(...receivers.map(r => r.data.length));
                const chartData = [];
                
                for (let i = 0; i < maxLength; i++) {
                    const dataPoint: any = { timestamp: null };
                    
                    receivers.forEach(receiver => {
                        if (i < receiver.data.length) {
                            dataPoint.timestamp = receiver.data[i].timestamp;
                            dataPoint[`receiver${receiver.id}`] = receiver.data[i].value;
                        }
                    });
                    
                    if (dataPoint.timestamp !== null) {
                        chartData.push(dataPoint);
                    }
                }
                
                return { 
                    data: chartData, 
                    hasRealTimeData: true, 
                    activeReceivers: receivers 
                };
            } else {
                // Fallback to static data from device.latest_status
                const values = device.latest_status?.gnss_samples ?? [];
                if (!Array.isArray(values) || values.length === 0) {
                    return { data: [], hasRealTimeData: false, activeReceivers: [] };
                }

                const staticData = values.slice(-600).map((value, index) => ({
                    timestamp: Date.now() - (values.length - index) * 1000,
                    receiver1: typeof value === 'number' ? value : 0,
                }));
                
                return { 
                    data: staticData, 
                    hasRealTimeData: false, 
                    activeReceivers: [{ id: 1, label: 'GNSS 1' }] 
                };
            }
        } else {
            // OCXO type
            if (chartData.ocxo.length > 0) {
                // Use real-time accumulated data
                const ocxoData = chartData.ocxo.map(point => ({
                    timestamp: point.timestamp,
                    value: point.value,
                }));
                
                return { 
                    data: ocxoData, 
                    hasRealTimeData: true, 
                    activeReceivers: [] 
                };
            } else {
                // Fallback to static data from device.latest_status
                const values = device.latest_status?.ocxo_control ?? [];
                if (!Array.isArray(values) || values.length === 0) {
                    return { data: [], hasRealTimeData: false, activeReceivers: [] };
                }

                const staticData = values.slice(-600).map((value, index) => ({
                    timestamp: Date.now() - (values.length - index) * 1000,
                    value: typeof value === 'number' ? value : 0,
                }));
                
                return { 
                    data: staticData, 
                    hasRealTimeData: false, 
                    activeReceivers: [] 
                };
            }
        }
    }, [type, device.latest_status, chartData, getActiveGnssReceivers]);

    // Calculate statistics for display
    const stats = useMemo(() => {
        if (data.length === 0) return { min: 0, max: 0, avg: 0, last: 0 };
        
        let values: number[] = [];
        
        if (type === 'gnss' && activeReceivers.length > 0) {
            // For GNSS, use the first active receiver for stats
            const firstReceiver = `receiver${activeReceivers[0].id}`;
            values = data.map(d => d[firstReceiver]).filter(v => v !== undefined);
        } else if (type === 'ocxo') {
            values = data.map(d => d.value).filter(v => v !== undefined);
        }
        
        if (values.length === 0) return { min: 0, max: 0, avg: 0, last: 0 };
        
        const min = Math.min(...values);
        const max = Math.max(...values);
        const sum = values.reduce((a, b) => a + b, 0);
        const avg = sum / values.length;
        const last = values[values.length - 1];
        
        return { min, max, avg, last };
    }, [data, type, activeReceivers]);

    const title = type === 'gnss' ? 'GNSS Samples (Real-time)' : 'OCXO Control (Real-time)';
    const chartColor = type === 'gnss' 
        ? theme.palette.primary.main 
        : theme.palette.secondary.main;

    // GNSS receiver colors
    const gnssColors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
    ];

    // Icon based on trend
    const TrendIcon = stats.last > stats.avg 
        ? TrendingUpIcon 
        : TrendingDownIcon;
    const trendIconColor = stats.last > stats.avg 
        ? theme.palette.success.main 
        : theme.palette.error.main;

    // Don't render if we have no data
    if (data.length === 0) {
        return (
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    height: '100%',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimelineIcon sx={{ mr: 1, color: chartColor }} />
                    <Typography variant="h6">
                        {title}
                    </Typography>
                </Box>
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: 'calc(100% - 40px)',
                        color: 'text.secondary',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <SpeedIcon sx={{ fontSize: 60, opacity: 0.5 }} />
                    <Typography>No real-time data available</Typography>
                    <Typography variant="body2">Waiting for MQTT data...</Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 3, 
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ mr: 1, color: chartColor }} />
                <Typography variant="h6">
                    {title}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {hasRealTimeData && (
                    <Chip 
                        label="LIVE"
                        size="small"
                        color="success"
                        sx={{ 
                            fontWeight: 500,
                            mr: 1,
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                                '100%': { opacity: 1 }
                            }
                        }}
                    />
                )}
                <Chip 
                    icon={<TrendIcon style={{ color: trendIconColor }} />}
                    label={`Current: ${stats.last.toFixed(4)}`}
                    size="small"
                    sx={{ 
                        fontWeight: 500,
                        bgcolor: 'background.default',
                    }}
                />
            </Box>

            {/* Statistics row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            bgcolor: 'background.default', 
                            borderRadius: 2,
                            textAlign: 'center', 
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            MIN
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {stats.min.toFixed(4)}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            bgcolor: 'background.default', 
                            borderRadius: 2,
                            textAlign: 'center', 
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            AVG
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {stats.avg.toFixed(4)}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            bgcolor: 'background.default', 
                            borderRadius: 2,
                            textAlign: 'center', 
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            MAX
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {stats.max.toFixed(4)}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Data points counter */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                    Showing {data.length} data points
                    {type === 'gnss' && activeReceivers.length > 1 && ` • ${activeReceivers.length} receivers`}
                    {hasRealTimeData ? ' (Real-time)' : ' (Historical)'}
                </Typography>
            </Box>

            <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{
                            top: 10,
                            right: 20,
                            left: 20,
                            bottom: 60,
                        }}
                    >
                        <CartesianGrid 
                            strokeDasharray="3 3" 
                            vertical={false} 
                            strokeOpacity={0.5}
                            stroke={theme.palette.divider}
                        />
                        
                        <XAxis
                            dataKey="timestamp"
                            type="number"
                            scale="time"
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={(value) => format(new Date(value), 'HH:mm:ss')}
                            label={{ 
                                value: 'Time', 
                                position: 'insideBottom', 
                                offset: -40,
                                style: { fill: theme.palette.text.secondary, fontSize: 12 }
                            }}
                            tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                            axisLine={{ stroke: theme.palette.divider }}
                        />
                        
                        <YAxis
                            label={{
                                value: type === 'gnss' ? 'GNSS Samples (ps)' : 'OCXO Control',
                                angle: -90,
                                position: 'insideLeft',
                                style: { 
                                    textAnchor: 'middle',
                                    fill: theme.palette.text.secondary, 
                                    fontSize: 12
                                }
                            }}
                            tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
                            axisLine={{ stroke: theme.palette.divider }}
                            domain={['auto', 'auto']}
                        />
                        
                        <RechartsTooltip 
                            content={<CustomTooltip />} 
                            cursor={{ 
                                stroke: theme.palette.text.secondary, 
                                strokeDasharray: '3 3',
                                strokeOpacity: 0.7
                            }} 
                        />
                        
                        {/* Reference line for average */}
                        <ReferenceLine 
                            y={stats.avg} 
                            stroke={theme.palette.info.main} 
                            strokeDasharray="3 3"
                            strokeOpacity={0.7}
                        >
                            <Label 
                                value="Average" 
                                position="insideBottomRight" 
                                style={{ 
                                    fill: theme.palette.info.main, 
                                    fontSize: 11,
                                    fontWeight: 500
                                }} 
                            />
                        </ReferenceLine>
                        
                        {/* Legend for multiple GNSS receivers */}
                        {type === 'gnss' && activeReceivers.length > 1 && (
                            <Legend />
                        )}
                        
                        {/* Render lines based on type */}
                        {type === 'gnss' ? (
                            // Multiple lines for GNSS receivers
                            activeReceivers.map((receiver, index) => (
                                <Line
                                    key={receiver.id}
                                    type="monotone"
                                    dataKey={`receiver${receiver.id}`}
                                    stroke={gnssColors[index % gnssColors.length]}
                                    strokeWidth={2}
                                    dot={false}
                                    name={receiver.label}
                                    activeDot={{ 
                                        r: 6, 
                                        stroke: theme.palette.background.paper, 
                                        strokeWidth: 2,
                                        fill: gnssColors[index % gnssColors.length] 
                                    }}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                            ))
                        ) : (
                            // Single line for OCXO
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke={chartColor}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ 
                                    r: 6, 
                                    stroke: theme.palette.background.paper, 
                                    strokeWidth: 2,
                                    fill: chartColor 
                                }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
};

export default DataVisualization; 